// Everything this feature feeds to (or checks against) the model arrives as HTML: ProVet stores
// both `text_template.text` and `consultationnote.text` as rich text, so a clinic template is a
// `<table>` of `<td>` cells and a vet's note is a run of `<p>`/`<li>`. Nothing here touches the DOM
// -- these are pure string helpers so they run identically in a Vitest node environment and in the
// browser, and so `template-placeholder.util.ts` can stay framework-free.

// Only the entities that actually occur in the clinic's own templates and notes, plus the four
// that any editor emits. `&nbsp;` is by far the most important: it is what makes an option like
// `Normal&nbsp; /&nbsp; Ligera DH 6%` fail a naive `includes()` against the model's answer.
const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", ndash: '–', mdash: '—',
  hellip: '…', deg: '°', ordm: 'º', ordf: 'ª', middot: '·', laquo: '«', raquo: '»',
  aacute: 'á', eacute: 'é', iacute: 'í', oacute: 'ó', uacute: 'ú', ntilde: 'ñ', uuml: 'ü',
  Aacute: 'Á', Eacute: 'É', Iacute: 'Í', Oacute: 'Ó', Uacute: 'Ú', Ntilde: 'Ñ', Uuml: 'Ü',
};

const ENTITY_PATTERN = /&(#x?[0-9a-f]+|[a-z][a-z0-9]*);/gi;

export function decodeHtmlEntities(text: string): string {
  return text.replace(ENTITY_PATTERN, (raw, body: string) => {
    if (body.startsWith('#')) {
      const codePoint = body[1] === 'x' || body[1] === 'X'
        ? Number.parseInt(body.slice(2), 16)
        : Number.parseInt(body.slice(1), 10);
      return Number.isFinite(codePoint) && codePoint > 0 ? String.fromCodePoint(codePoint) : raw;
    }

    return NAMED_ENTITIES[body] ?? raw;
  });
}

// `<td>` becomes " | " rather than a newline because a template's exploration table puts the label
// and its gap in sibling cells of the SAME row -- flattening them to separate lines would destroy
// exactly the pairing `describePlaceholders` needs to tell the model what an empty `[[ ]]` is for.
const BLOCK_CLOSERS = /<\/(?:p|div|h[1-6]|tr|ul|ol|table|tbody|thead|blockquote|section|article)\s*>/gi;

export function htmlToPlainText(html: string): string {
  const withBreaks = html
    .replace(/<(script|style)[\s\S]*?<\/\1\s*>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    // Only the OPENING tag breaks the line: `</li>` breaking it too would put a blank line between
    // every pair of bullets, and an exploration list is nine of them.
    .replace(/<li[^>]*>/gi, '\n- ')
    .replace(/<\/t[dh]\s*>/gi, ' | ')
    .replace(BLOCK_CLOSERS, '\n')
    .replace(/<[^>]*>/g, '');

  return collapseWhitespace(decodeHtmlEntities(withBreaks));
}

function collapseWhitespace(text: string): string {
  return text
    .split('\n')
    // \s covers the non-breaking space `&nbsp;` decoded to U+00A0 as well as ordinary runs.
    .map((line) => line.replace(/\s+/g, ' ').replace(/(?:\s*\|\s*)+$/, '').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Whitespace is REMOVED rather than collapsed: the only consumer is `verifyFilledTemplate`, whose
// job is to decide whether the clinic's wording survived in the model's answer. The model reflows
// and re-indents HTML freely, so any whitespace-sensitive comparison reports an alteration that a
// vet reading both texts would never call one. Accents are kept -- "higado" and "hígado" are a real
// difference in a clinical note, not a formatting one.
export function toComparableText(html: string): string {
  return htmlToPlainText(html).normalize('NFC').toLocaleLowerCase('es').replace(/\s+/g, '');
}
